import { useCallback, useEffect, useMemo, useRef, useState } from "react";

interface PlayingIndexIsh {
  lineIndex: number;
  tokenIndeces: number[];
}

type Ish = {
  lineIndex: number;
  tokenIndex: number;
  token: NavToken;
};

//TODO: to be renamed, maybe/probably
interface NavToken {
  timeStamp?: number;
  canJumpTo?: boolean;
  isNavable: boolean;
}

export interface NavLine {
  tokens: NavToken[];
}

//you give me:
//  a) what lines look like
//  b) power to seek in video
//  c) the current timestamp
//I'll give you:
//  a) callbacks for how to seek to various places
//  b) what's currently focused

function useNavigableLines(
  lines: NavLine[],
  timestamp?: number,
  seekToTs?: (ts: number) => void
): {
  seekToPrevLine: () => void;
  seekToFocusedToken: () => void;
  focusPrevJumpableToken: () => void;
  focusNextJumpableToken: () => void;
  focusSpecificToken: (lineIndex: number, tokenIndex: number) => void;

  focusPrevWord: () => void;
  focusNextWord: () => void;
  focusPrevLine: () => void;
  focusNextLine: () => void;
  focusedLineIndex: number;
  focusedTokenIndex: number;
} {
  const jsonLines = JSON.stringify(lines);
  const [focusedLineIndex, setFocusedLineIndex] = useState(0);
  //atow hasUserRecentlyNaved being used only for not changing focused token
  const [hasUserRecentlyNaved, setHasUserRecentlyNaved] = useState(false);
  const [hasUserRecentlCapitalEssed, setHasUserRecentlCapitalEssed] =
    useState(false);

  //start with index of first word token
  const [focusedTokenIndex, setFocusedTokenIndex] = useState<number>(() =>
    lines[0].tokens.findIndex((t) => t.isNavable)
  );

  const navTimeoutIdRef = useRef<null | number>(null);
  const capitalEssTimeoutIdRef = useRef<null | number>(null);

  const setUserHasNaved = useCallback(() => {
    if (navTimeoutIdRef.current !== null) {
      window.clearTimeout(navTimeoutIdRef.current);
    }

    setHasUserRecentlyNaved(true);
    const timeoutId = window.setTimeout(() => {
      setHasUserRecentlyNaved(false);
    }, 2500);
    navTimeoutIdRef.current = timeoutId;
  }, [setHasUserRecentlyNaved]);

  const setUserHasCapitalEssed = useCallback(() => {
    if (capitalEssTimeoutIdRef.current !== null) {
      window.clearTimeout(capitalEssTimeoutIdRef.current);
    }

    setHasUserRecentlCapitalEssed(true);
    const timeoutId = window.setTimeout(() => {
      setHasUserRecentlCapitalEssed(false);
    }, 1500);
    capitalEssTimeoutIdRef.current = timeoutId;
  }, [setHasUserRecentlCapitalEssed]);

  const allWordIshes: Ish[] = useMemo(
    () =>
      lines
        .flatMap((line, lineIndex) =>
          line.tokens.map((token, tokenIndex) => ({
            lineIndex,
            tokenIndex,
            token,
          }))
        )
        .filter((thing) => thing.token.isNavable),
    [lines]
  );

  const focusPrevWord = useCallback(() => {
    const wordIndexOfCurrentlyFocused = allWordIshes.findIndex(
      (thing) =>
        thing.lineIndex === focusedLineIndex &&
        thing.tokenIndex === focusedTokenIndex
    );
    if (wordIndexOfCurrentlyFocused > 0) {
      const newAbsLineIndex =
        allWordIshes[wordIndexOfCurrentlyFocused - 1].lineIndex;
      const newAbsTokenIndex =
        allWordIshes[wordIndexOfCurrentlyFocused - 1].tokenIndex;
      setFocusedLineIndex(newAbsLineIndex);
      setFocusedTokenIndex(newAbsTokenIndex);
      setUserHasNaved();
    }
  }, [focusedLineIndex, focusedTokenIndex, allWordIshes, setUserHasNaved]);

  const focusNextWord = useCallback(() => {
    const wordIndexOfCurrentlyFocused = allWordIshes.findIndex(
      (thing) =>
        thing.lineIndex === focusedLineIndex &&
        thing.tokenIndex === focusedTokenIndex
    );
    if (wordIndexOfCurrentlyFocused < allWordIshes.length - 1) {
      const newAbsLineIndex =
        allWordIshes[wordIndexOfCurrentlyFocused + 1].lineIndex;
      const newAbsTokenIndex =
        allWordIshes[wordIndexOfCurrentlyFocused + 1].tokenIndex;
      setFocusedLineIndex(newAbsLineIndex);
      setFocusedTokenIndex(newAbsTokenIndex);
      setUserHasNaved();
    }
  }, [focusedLineIndex, focusedTokenIndex, allWordIshes, setUserHasNaved]);

  const focusPrevLine = useCallback(() => {
    setFocusedLineIndex((prevLineIndex) => {
      const newLineIndex = Math.max(0, prevLineIndex - 1);
      setFocusedTokenIndex(
        lines[newLineIndex].tokens.findIndex((t) => t.isNavable)
      );
      return newLineIndex;
    });
    setUserHasNaved();
  }, [jsonLines, setUserHasNaved]);

  const focusNextLine = useCallback(() => {
    setFocusedLineIndex((prevLineIndex) => {
      const newLineIndex = Math.min(lines.length - 1, prevLineIndex + 1);
      setFocusedTokenIndex(
        lines[newLineIndex].tokens.findIndex((t) => t.isNavable)
      );
      return newLineIndex;
    });
    setUserHasNaved();
  }, [jsonLines, setUserHasNaved, setFocusedLineIndex, setFocusedTokenIndex]);

  const seekToPrevLine = useCallback(() => {
    if (seekToTs === undefined) {
      console.log("seekTo not passed in, not doing anything");
      return;
    }
    const newFocusedLineIndex = hasUserRecentlCapitalEssed
      ? Math.max(0, focusedLineIndex - 1)
      : focusedLineIndex;
    const newToken: Ish = allWordIshes.filter((ish) => {
      return ish.lineIndex === newFocusedLineIndex;
    })[0];
    setFocusedLineIndex(newFocusedLineIndex);
    setFocusedTokenIndex(newToken.tokenIndex);
    if (newToken.token.timeStamp) {
      //seekToTs(newToken.token.timeStamp);
      seekToTs(newToken.token.timeStamp + 1);
      setUserHasCapitalEssed();
    }
  }, [
    focusedLineIndex,
    allWordIshes,
    setFocusedLineIndex,
    setFocusedTokenIndex,
    hasUserRecentlCapitalEssed,
    seekToTs,
    setUserHasCapitalEssed,
  ]);

  const seekToFocusedToken = useCallback(() => {
    if (seekToTs === undefined) {
      console.log("seekTo not passed in, not doing anything");
      return;
    }
    const targetTs: number | undefined =
      lines[focusedLineIndex].tokens[focusedTokenIndex].timeStamp;
    if (targetTs === undefined) {
      console.log("can't seek, no ts at provided line and token indeces");
      return;
    }
    seekToTs(targetTs);
  }, [jsonLines, focusedLineIndex, focusedTokenIndex, seekToTs]);

  const focusPrevJumpableToken = useCallback(() => {
    const currentIndexIsh = allWordIshes.findIndex(
      (ish) =>
        ish.tokenIndex === focusedTokenIndex &&
        ish.lineIndex === focusedLineIndex
    );
    const tokensBefore = allWordIshes
      .slice(0, currentIndexIsh)
      .filter((ish) => {
        if (!ish.token.isNavable) {
          return false;
        }
        return ish.token.canJumpTo;
      });

    if (tokensBefore.length > 0) {
      const previousIsh = tokensBefore[tokensBefore.length - 1];
      setFocusedLineIndex(previousIsh.lineIndex);
      setFocusedTokenIndex(previousIsh.tokenIndex);
      setUserHasNaved();
    }
  }, [focusedLineIndex, focusedTokenIndex, allWordIshes, setUserHasNaved]);

  const focusNextJumpableToken = useCallback(() => {
    const currentIndexIsh = allWordIshes.findIndex(
      (ish) =>
        ish.tokenIndex === focusedTokenIndex &&
        ish.lineIndex === focusedLineIndex
    );
    const tokensAfter = allWordIshes
      .slice(currentIndexIsh + 1)
      .filter((ish) => {
        if (!ish.token.isNavable) {
          return false;
        }
        return ish.token.canJumpTo;
      });
    if (tokensAfter.length > 0) {
      const nextIsh = tokensAfter[0];
      setFocusedLineIndex(nextIsh.lineIndex);
      setFocusedTokenIndex(nextIsh.tokenIndex);
      setUserHasNaved();
    }
  }, [focusedLineIndex, focusedTokenIndex, allWordIshes, setUserHasNaved]);

  const focusSpecificToken = useCallback(
    (lineIndex: number, tokenIndex: number) => {
      setFocusedLineIndex(lineIndex);
      setFocusedTokenIndex(tokenIndex);
    },
    []
  );

  useEffect(() => {
    //update focused word (atow what's in light green border) if timestamp has changed
    //and user has not recently naved (with keyboard)
    if (timestamp === undefined || hasUserRecentlyNaved) {
      return;
    }

    const playingIshes: PlayingIndexIsh[] = lines
      .map((l, lineIndex) => {
        const matchingTokenIndeces: number[] = l.tokens
          .map((t, tokenIndex) =>
            t.timeStamp === timestamp || t.timeStamp === timestamp - 1
              ? tokenIndex
              : undefined
          )
          .filter((i) => i !== undefined) as number[];
        return matchingTokenIndeces.length > 0
          ? { lineIndex, tokenIndeces: matchingTokenIndeces }
          : undefined;
      })
      .filter((l) => l !== undefined) as PlayingIndexIsh[];
    const focusedLine: PlayingIndexIsh | undefined = getMiddle(playingIshes);

    const newFocusedLineIndex: number | undefined = focusedLine?.lineIndex;
    //have new focused word be middle word in blob of words that are
    //considered to be playing now
    const newFocusedTokenIndex: number | undefined =
      focusedLine === undefined
        ? undefined
        : getMiddle(focusedLine?.tokenIndeces);

    if (
      newFocusedLineIndex !== undefined &&
      newFocusedTokenIndex !== undefined
    ) {
      setFocusedLineIndex(newFocusedLineIndex);
      setFocusedTokenIndex(newFocusedTokenIndex);
    }
  }, [jsonLines, timestamp, lines.length, hasUserRecentlyNaved]);

  return {
    seekToPrevLine,
    seekToFocusedToken,
    focusNextJumpableToken,
    focusPrevJumpableToken,
    focusPrevLine,
    focusNextLine,
    focusPrevWord,
    focusNextWord,
    focusedLineIndex,
    focusedTokenIndex,
    focusSpecificToken,
  };
}

export default useNavigableLines;

function getMiddle<T>(list: T[]): T | undefined {
  const length = list.length;
  if (length === 0) return undefined;

  const middleIndex = Math.floor(length / 2);
  return list[middleIndex];
}
